import { AlertRepository } from "../../repositories/AlertRrepository";
import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  Grid,
  Icon,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useMediaQuery, useTheme } from "@mui/system";
import GvmsDeclarationForm from "./GvmsDeclarationForm";
import { SETTINGS } from "../../properties/Settings";
import moment from "moment";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider as LocalizationProvider1 } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { GvmsRepository } from "../../repositories/GvmsRepository";
import { OrganizationsRepository } from "../../repositories/OrganizationsRepository";
import { AuthRepository } from "../../repositories/AuthRepository";

const emptyFilters = {
  organizationName: "",
  vehicleRegNum: "",
  departurePort: "",
  arrivalPort: "",
  crossingDepartureDateTime: "",
  carrierCompany: "",
  gmrState: "",
};

export default function GvmsDeclarations() {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("lg"));

  const [declarations, setDeclarations] = useState([]);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const response = await AlertRepository.getAllActiveByType({ type: "GVMS" });
        setAlerts(response.data);
      } catch (error) {
        console.error("Failed to fetch alerts: ", error);
      }
    };
    fetchAlerts();
  }, []);
  const defaultColors = {
    success: { bg: "#edf7ed", text: "#1e4620", icon: "#2e7d32" },
    error: { bg: "#fdecea", text: "#611a15", icon: "#d32f2f" },
    warning: { bg: "#fff4e5", text: "#663c00", icon: "#ed6c02" },
    info: { bg: "#e8f4fd", text: "#014361", icon: "#0288d1" },
  };


  const [openForm, setOpenForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deletingDeclaration, setDeletingDeclaration] = useState(null);
  const [deletingFromConex, setDeletingFromConex] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [totalElements, setTotalElements] = useState(0);

  const [filters, setFilters] = useState(emptyFilters);
  const [activeFilters, setActiveFilters] = useState(emptyFilters);
  const [expanded, setExpanded] = useState(false);

  const [orderBy, setOrderBy] = useState("gmrState");
  const [orderDirection, setOrderDirection] = useState("ASC");

  const [departurePortOptions, setDeparturePortOptions] = useState([]);
  const [arrivalPortOptions, setArrivalPortOptions] = useState([]);
  const [carrierOptions, setCarrierOptions] = useState([]);
  const [routes, setRoutes] = useState([]);

  const [clientInputValue, setClientInputValue] = useState("");
  const [clientOptions, setClientOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);

  const [currentUserOrganization, setCurrentUserOrganization] = useState(null);
  const [userFirstName, setUserFirsName] = useState("");
  const [userLastName, setUserLastName] = useState("");

  const [highlightedRow, setHighlightedRow] = useState(sessionStorage.getItem("highlightedRow"));

  const navigate = useNavigate();

  const filteredDepartureOptions = filters.arrivalPort
    ? [
        ...new Set(
          routes
            .filter((r) => r.destinationPort === filters.arrivalPort)
            .map((r) => r.departurePort),
        ),
      ]
    : departurePortOptions;

  const filteredArrivalOptions = filters.departurePort
    ? [
        ...new Set(
          routes
            .filter((r) => r.departurePort === filters.departurePort)
            .map((r) => r.destinationPort),
        ),
      ]
    : arrivalPortOptions;

  const filteredCarrierOptions =
    filters.departurePort || filters.arrivalPort
      ? [
          ...new Set(
            routes
              .filter((r) => {
                return (
                  (!filters.departurePort ||
                    r.departurePort === filters.departurePort) &&
                  (!filters.arrivalPort ||
                    r.destinationPort === filters.arrivalPort)
                );
              })
              .map((r) => r.carrier),
          ),
        ]
      : carrierOptions;

  useEffect(() => {
    fetchDeclarations();
  }, [page, rowsPerPage, activeFilters, orderBy, orderDirection]);

  useEffect(() => {
    const userDetails = AuthRepository.getUserDetails();
    setCurrentUserOrganization(userDetails?.organization ?? null);
    setUserFirsName(userDetails?.firstName ?? null);
    setUserLastName(userDetails?.lastName ?? null);
  }, []);

  useEffect(() => {
    const id = sessionStorage.getItem("highlightedRow");
    if (id) {
      setHighlightedRow(id);
    }
  }, []);

  useEffect(() => {
    GvmsRepository.getRoutes()
      .then((res) => {
        setRoutes(res.data);

        const departures = [
          ...new Set(res.data.map((r) => r.departurePort)),
        ].sort();
        const arrivals = [
          ...new Set(res.data.map((r) => r.destinationPort)),
        ].sort();
        const carriers = [...new Set(res.data.map((r) => r.carrier))].sort();

        setDeparturePortOptions(departures);
        setArrivalPortOptions(arrivals);
        setCarrierOptions(carriers);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (clientInputValue.length < 3) {
      setClientOptions([]);
      return;
    }

    OrganizationsRepository.getBySearchString(clientInputValue)
      .then((res) => {
        const clients = res.data.map((o) => o.nameEn);
        setClientOptions(clients);
      })
      .catch(console.error);
  }, [clientInputValue]);

  useEffect(() => {
    const hasThreeCharField = Object.values(filters).some(
      (v) => typeof v === "string" && v.length >= 3,
    );
    const allEmpty = Object.values(filters).every((v) => !v);

    if (hasThreeCharField || allEmpty) {
      setPage(0);
      setActiveFilters({ ...filters });
    }
  }, [filters]);

  useEffect(() => {
    const pendingId = sessionStorage.getItem("openDeclarationId");
    if (pendingId) {
      sessionStorage.removeItem("openDeclarationId");
      setEditingId(pendingId);
      setOpenForm(true);
    }
  }, []);

  const fetchDeclarations = async () => {
    try {
      setLoading(true);

      const searchParamsObj = {};
      Object.entries(activeFilters).forEach(([key, value]) => {
        if (value) searchParamsObj[key] = value;
      });

      const res = await GvmsRepository.getAllDeclarations({
        page,
        size: rowsPerPage,
        orderBy,
        orderDirection,
        searchParams: JSON.stringify(searchParamsObj),
      });

      setDeclarations(res.data.content || []);
      setTotalElements(res.data.totalElements || 0);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => {
      const updated = { ...prev, [field]: value };

      if (field === "departurePort" && updated.arrivalPort) {
        const valid = routes.some(
          (r) =>
            r.departurePort === value &&
            r.destinationPort === updated.arrivalPort,
        );
        if (!valid) updated.arrivalPort = "";
      }

      if (field === "arrivalPort" && updated.departurePort) {
        const valid = routes.some(
          (r) =>
            r.destinationPort === value &&
            r.departurePort === updated.departurePort,
        );
        if (!valid) updated.departurePort = "";
      }

      return updated;
    });
  };

  const handleDuplicate = () => {
    fetchDeclarations();
    if (editingId) {
      GvmsRepository.getById(editingId)
        .then((res) => setEditingId(res.data?.id))
        .catch(console.error);
    }
  };

  const handleClickChangeOrderBy = (field) => {
    if (orderBy === field) {
      setOrderDirection(orderDirection === "ASC" ? "DESC" : "ASC");
    } else {
      setOrderBy(field);
      setOrderDirection("DESC");
    }
    setPage(0);
  };

  const handleFormClose = () => {
    if (editingId) {
      setDeclarations((prev) =>
        prev.map((d) => (d.id === editingId ? { ...d, seen: true } : d)),
      );
    }
    setOpenForm(false);
    setEditingId(null);
  };

  const handleFormSuccess = () => {
    fetchDeclarations();
    setOpenForm(false);
    setEditingId(null);
  };

  const handleExport = async () => {
    setLoadingExport(true);
    try {
      const searchParamsObj = {};
      Object.entries(activeFilters).forEach(([key, value]) => {
        if (value) searchParamsObj[key] = value;
      });

      const response = await GvmsRepository.exportToXlsx(
        JSON.stringify(searchParamsObj),
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "gvms-declarations.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingExport(false);
    }
  };

  const handleDelete = async () => {
    try {
      await GvmsRepository.deleteDeclaration(deletingDeclaration.id);
      setDeletingDeclaration(null);
      fetchDeclarations();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteFromConex = async () => {
    try {
      await GvmsRepository.deleteFromConex(deletingFromConex.id);
      setDeletingFromConex(null);
      fetchDeclarations();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDownloadPdf = async (declarationId) => {
    try {
      const response = await GvmsRepository.downloadPdf(declarationId);

      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `declaration-${declarationId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download PDF:", err);
    }
  };

  const checkNumberOfFilters = () => {
    let numberOfFilters = 0;
    Object.keys(activeFilters).forEach((key) => {
      if (
        activeFilters[key] !== undefined &&
        activeFilters[key] !== "" &&
        activeFilters[key] !== null
      ) {
        numberOfFilters++;
      }
    });
    return numberOfFilters;
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status) => {
    if (
      status === "ERROR" ||
      status === "NOT_FINALISABLE" ||
      status === "REJECTED"
    )
      return "error";
    if (status === "COMPLETED" || status === "FINALISED") return "success";
    if (status === "DRAFT") return "warning";
    return "primary";
  };

  return (
    <>
      <Grid container>
        {alerts.map((alert) => (
          <Grid item xs={12} sx={{ marginBottom: 2 }} key={alert.id}>
            <Alert
              severity={alert.severity}
              sx={{
                fontSize: "0.9rem",
                backgroundColor: defaultColors[alert.severity].bg,
                color: defaultColors[alert.severity].text,
                "& .MuiAlert-icon": {
                  color: defaultColors[alert.severity].icon,
                },
              }}
            >
              {alert.description}
            </Alert>
          </Grid>
        ))}

        {/* Header row - matches ConsignmentList 4/8 split */}
        <Grid item xs={12} md={4}>
          <Stack
            direction="row"
            sx={{ display: "flex", justifyContent: "space-between" }}
          >
            <Typography
              variant="h5"
              key="2"
              color="text.primary"
              sx={{
                fontWeight: "bold",
                typography: { xs: "h4", md: "h5" },
                alignContent: "center",
                marginLeft: { xs: "8px", md: "0px" },
              }}
            >
              GVMS
              <IconButton
                onClick={fetchDeclarations}
                sx={{
                  border: "1px solid lightGray",
                  borderRadius: "5px",
                  padding: "1px",
                  minWidth: "auto",
                  height: "auto",
                  marginLeft: "5px",
                  display: { xs: "none", md: "inline-flex" },
                }}
              >
                <Icon
                  style={{ color: theme.palette.darkContrast.main }}
                  fontSize="20"
                >
                  refresh
                </Icon>
              </IconButton>
              <IconButton
                onClick={() => {
                  setEditingId(null);
                  setOpenForm(true);
                }}
                sx={{
                  border: "1px solid lightGray",
                  borderRadius: "5px",
                  padding: "1px",
                  minWidth: "auto",
                  height: "auto",
                  marginLeft: "5px",
                  display: { xs: "none", md: "inline-flex" },
                }}
              >
                <Icon
                  style={{ color: theme.palette.darkContrast.main }}
                  fontSize="20"
                >
                  add
                </Icon>
              </IconButton>
            </Typography>
            <Box
              sx={{
                display: { xs: "inline-flex", md: "none", marginRight: "11px" },
              }}
            >
              <IconButton
                onClick={fetchDeclarations}
                sx={{
                  border: "1px solid lightGray",
                  borderRadius: "5px",
                  padding: "1px",
                  minWidth: 55,
                  height: 55,
                  marginLeft: "5px",
                }}
              >
                <Icon
                  style={{
                    color: theme.palette.darkContrast.main,
                    fontSize: "28px",
                  }}
                >
                  refresh
                </Icon>
              </IconButton>
              <IconButton
                onClick={() => {
                  setEditingId(null);
                  setOpenForm(true);
                }}
                sx={{
                  border: "1px solid lightGray",
                  borderRadius: "5px",
                  padding: "1px",
                  minWidth: 55,
                  height: 55,
                  marginLeft: "5px",
                }}
              >
                <Icon
                  style={{
                    color: theme.palette.darkContrast.main,
                    fontSize: "28px",
                  }}
                >
                  add
                </Icon>
              </IconButton>
            </Box>
          </Stack>
        </Grid>
        <Grid item md={8} sx={{ textAlign: "right" }}></Grid>

        <Grid item xs={12} sx={{ paddingTop: 2 }}>
          <Divider />
        </Grid>

        {/* Filters - matching ConsignmentList Accordion style */}
        <Grid item xs={12}>
          <Accordion
            style={{
              boxShadow: "none",
              backgroundColor: "transparent",
            }}
            expanded={expanded}
          >
            <AccordionSummary
              style={{
                cursor: "default",
                backgroundColor: theme.palette.dashboardBackground.main,
              }}
            >
              <Grid container alignItems="center" sx={{ width: "100%" }}>
                {/* Filters button - left side */}
                <Grid item xs={4} md={2}>
                  {isSmallScreen ? (
                    <Box sx={{ width: "100%", position: "relative" }}>
                      <Button
                        fullWidth
                        color="primary"
                        variant={expanded ? "contained" : "outlined"}
                        onClick={() => setExpanded(!expanded)}
                        sx={{
                          height: "50px",
                          fontSize: "15px",
                          marginTop: "16px",
                        }}
                        endIcon={
                          expanded ? (
                            <Tooltip
                              title={"Clear filters"}
                              arrow
                              placement="right"
                            >
                              <IconButton
                                size="small"
                                sx={{
                                  color: theme.palette.dashboardBackground.main,
                                  fontSize: "15px",
                                  width: "10px",
                                  marginLeft: "5px",
                                }}
                                onClick={() => {
                                  setFilters(emptyFilters);
                                  setActiveFilters(emptyFilters);
                                  setPage(0);
                                }}
                              >
                                <Icon>clear</Icon>
                              </IconButton>
                            </Tooltip>
                          ) : null
                        }
                      >
                        F
                        <span style={{ textTransform: "lowercase" }}>
                          ilters
                        </span>
                      </Button>
                      {checkNumberOfFilters() > 0 && (
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: -8,
                            right: -8,
                            backgroundColor: theme.palette.primary.main,
                            color: "white",
                            borderRadius: 1,
                            fontSize: "12px",
                            padding: "0 6px",
                            minWidth: "20px",
                            height: "20px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "1px solid",
                            borderColor: expanded
                              ? theme.palette.dashboardBackground.main
                              : "none",
                          }}
                        >
                          {checkNumberOfFilters()}
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Button
                      color="primary"
                      variant={expanded ? "contained" : "outlined"}
                      onClick={() => setExpanded(!expanded)}
                      style={{
                        transition: "width 0.3s ease",
                        width: expanded ? "125px" : "100px",
                        height: "50px",
                        fontSize: "15px",
                        marginTop: "16px",
                      }}
                      endIcon={
                        expanded ? (
                          <Tooltip
                            title={"Clear filters"}
                            arrow
                            placement="right"
                          >
                            <IconButton
                              size="small"
                              sx={{
                                color: theme.palette.dashboardBackground.main,
                                fontSize: "15px",
                                width: "10px",
                                marginLeft: "5px",
                              }}
                              onClick={() => {
                                setFilters(emptyFilters);
                                setActiveFilters(emptyFilters);
                                setPage(0);
                              }}
                            >
                              <Icon>clear</Icon>
                            </IconButton>
                          </Tooltip>
                        ) : (
                          ""
                        )
                      }
                    >
                      F
                      <span style={{ textTransform: "lowercase" }}>ilters</span>
                    </Button>
                  )}
                </Grid>

                {/* Export button - right side */}
                <Grid
                  item
                  xs={8}
                  md={10}
                  sx={{ display: "flex", justifyContent: "flex-end" }}
                >
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleExport}
                    disabled={loadingExport}
                    startIcon={
                      loadingExport ? (
                        <CircularProgress size={20} />
                      ) : (
                        <Icon>download</Icon>
                      )
                    }
                    sx={{ height: "50px", fontSize: "15px", marginTop: "16px" }}
                  >
                    Export to Excel
                  </Button>
                </Grid>
              </Grid>
            </AccordionSummary>
            <AccordionDetails
              style={{
                paddingLeft: isSmallScreen ? "9px" : "1px",
                marginRight: isSmallScreen ? "-5px" : "0px",
                backgroundColor: theme.palette.dashboardBackground.main,
                marginTop: "-18px",
              }}
            >
              <Grid container spacing={1}>
                <Grid item xs={12} md={1.7} lg={1.2}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>GMR Status</InputLabel>
                    <Select
                      label="GMR Status"
                      value={filters.gmrState}
                      onChange={(e) =>
                        handleFilterChange("gmrState", e.target.value)
                      }
                      color="lightGray"
                      disabled={loading}
                      style={{
                        width: "100%",
                        fontSize: "12px",
                        borderRadius: "5px",
                        fontWeight: "bold",
                      }}
                    >
                      <MenuItem value="">
                        <span style={{ marginLeft: 10 }}>ALL</span>
                      </MenuItem>
                      {[
                        "OPEN",
                        "CHECKED_IN",
                        "EMBARKED",
                        "FINALISED",
                        "COMPLETED",
                        "CREATED",
                        "SENT",
                        "NOT_FINALISABLE",
                        "ERROR",
                        "DRAFT",
                        "REJECTED",
                      ].map((s) => (
                        <MenuItem key={s} value={s}>
                          <span style={{ marginLeft: 10 }}>{s}</span>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={1.7} lg={1.2}>
                  <TextField
                    label="Vehicle Reg"
                    fullWidth
                    variant="outlined"
                    value={filters.vehicleRegNum}
                    onChange={(e) =>
                      handleFilterChange("vehicleRegNum", e.target.value)
                    }
                  />
                </Grid>

                <Grid item xs={12} md={1.7} lg={1.2}>
                  <Autocomplete
                    options={filteredDepartureOptions}
                    value={filters.departurePort || null}
                    onChange={(_, value) =>
                      handleFilterChange("departurePort", value ?? "")
                    }
                    noOptionsText="No ports found"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Departure Port"
                        fullWidth
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={1.7} lg={1.2}>
                  <Autocomplete
                    options={filteredArrivalOptions}
                    value={filters.arrivalPort || null}
                    onChange={(_, value) =>
                      handleFilterChange("arrivalPort", value ?? "")
                    }
                    noOptionsText="No ports found"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Arrival Port"
                        fullWidth
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={1.7} lg={1.2}>
                  <Autocomplete
                    options={filteredCarrierOptions}
                    value={filters.carrierCompany || null}
                    onChange={(_, value) =>
                      handleFilterChange("carrierCompany", value ?? "")
                    }
                    noOptionsText="No carriers found"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Carrier"
                        fullWidth
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Mobile view  */}
        <Grid
          item
          xs={12}
          sx={{
            display: {
              xs: "block",
              md: "none",
            },
            height: "75vh",
            overflowY: "auto",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          <Table
            sx={{
              borderSpacing: "8px",
              borderCollapse: "separate",
              paddingBottom: "10px",
            }}
          >
            <TableBody>
              {loading
                ? Array.from({ length: rowsPerPage }).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell colSpan={14} sx={{ padding: "12px 16px" }}>
                        <Skeleton
                          variant="rounded"
                          width="100%"
                          height={25}
                          animation="wave"
                        />
                      </TableCell>
                    </TableRow>
                  ))
                : declarations.length > 0 &&
                  declarations.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell colSpan={13} sx={{ padding: { xs: 1, sm: 2 } }}>
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Typography
                              variant="h5"
                              sx={{ fontWeight: "bold", fontSize: "16px" }}
                            >
                              Created Date
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              variant="h5"
                              sx={{ fontWeight: "light", fontSize: "16px" }}
                            >
                              {item?.dateCreated
                                ? moment(item?.dateCreated).format(
                                    "DD.MM.YYYY HH:mm",
                                  )
                                : "N/A"}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              variant="h5"
                              sx={{ fontWeight: "bold", fontSize: "16px" }}
                            >
                              Created By
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              variant="h5"
                              sx={{ fontWeight: "light", fontSize: "16px" }}
                            >
                              {`${item?.createdBy?.firstName ?? ""} ${item?.createdBy?.lastName ?? ""}`}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              variant="h5"
                              sx={{ fontWeight: "bold", fontSize: "16px" }}
                            >
                              Client
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              variant="h5"
                              sx={{ fontWeight: "light", fontSize: "16px" }}
                            >
                              {item?.organization?.nameEn ?? "N/A"}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              variant="h5"
                              sx={{ fontWeight: "bold", fontSize: "16px" }}
                            >
                              Vehicle
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              variant="h5"
                              sx={{ fontWeight: "light", fontSize: "16px" }}
                            >
                              {item?.transport?.vehicleRegNum || "N/A"}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              variant="h5"
                              sx={{ fontWeight: "bold", fontSize: "16px" }}
                            >
                              Departure Port
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              variant="h5"
                              sx={{ fontWeight: "light", fontSize: "16px" }}
                            >
                              {item?.transport?.departurePort || "N/A"}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              variant="h5"
                              sx={{ fontWeight: "bold", fontSize: "16px" }}
                            >
                              Arrival Port
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              variant="h5"
                              sx={{ fontWeight: "light", fontSize: "16px" }}
                            >
                              {item?.transport?.arrivalPort || "N/A"}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              variant="h5"
                              sx={{ fontWeight: "bold", fontSize: "16px" }}
                            >
                              Crossing Date
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              variant="h5"
                              sx={{ fontWeight: "light", fontSize: "16px" }}
                            >
                              {item?.transport?.crossingDepartureDateTime
                                ? moment(
                                    item?.transport?.crossingDepartureDateTime,
                                  ).format("DD.MM.YYYY HH:mm")
                                : "N/A"}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              variant="h5"
                              sx={{ fontWeight: "bold", fontSize: "16px" }}
                            >
                              GMR ID
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              variant="h5"
                              sx={{ fontWeight: "light", fontSize: "16px" }}
                            >
                              {item?.gmrId || "N/A"}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              variant="h5"
                              sx={{ fontWeight: "bold", fontSize: "16px" }}
                            >
                              GMR Status
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Chip
                              label={item?.gmrState || "N/A"}
                              size="small"
                              color={getStatusColor(item?.gmrState)}
                            />
                          </Grid>
                          <Grid item xs={12} sx={{ textAlign: "right" }}>
                            {item?.gmrId && (
                              <Tooltip title="Download PDF">
                                <IconButton
                                  onClick={() => handleDownloadPdf(item?.id)}
                                >
                                  <Icon
                                    style={{
                                      color: theme.palette.secondary.main,
                                    }}
                                    fontSize="large"
                                  >
                                    download
                                  </Icon>
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Open declaration">
                              <IconButton
                                onClick={() => setEditingId(item?.id)}
                              >
                                <Icon
                                  style={{
                                    color: theme.palette.primary.main,
                                  }}
                                  fontSize="large"
                                >
                                  open_in_new
                                </Icon>
                              </IconButton>
                            </Tooltip>
                          </Grid>
                        </Grid>
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
            <TableFooter
              style={{
                position: "fixed",
                bottom: 0,
                zIndex: 5,
                backgroundColor: theme.palette.background.paper,
                width: "100%",
                left: 0,
                right: 0,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <TableRow>
                <TableCell colSpan={20} sx={{ borderBottom: "none" }}>
                  <Grid item xs={12} justifyContent="center"></Grid>
                  <Grid
                    container
                    sx={{
                      marginBottom: "10px",
                    }}
                  >
                    <Grid item xs={12}>
                      <Pagination
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          "& .MuiPagination-ul": {
                            display: "flex",
                            justifyContent: "center",
                            flexGrow: 1,
                          },
                          "& .MuiPaginationItem-root": {
                            fontSize: "24px",
                          },
                          "& .MuiPaginationItem-page": {
                            height: 48,
                            width: 48,
                          },
                          "& .MuiPaginationItem-page.Mui-selected": {
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.5,
                            ),
                            color: "white",
                            height: 48,
                            width: 48,
                          },
                          "& .MuiPaginationItem-previousNext": {
                            border: "1px solid",
                            borderColor: theme.palette.lightGray.main,
                            height: 48,
                            width: 48,
                            "& svg": {
                              fontSize: "24px",
                            },
                          },
                        }}
                        count={
                          (totalElements % rowsPerPage === 0
                            ? totalElements / rowsPerPage
                            : Math.floor(totalElements / rowsPerPage) + 1) || 0
                        }
                        shape="rounded"
                        size="small"
                        page={page + 1 || 0}
                        onChange={(event, value) => {
                          handleChangePage(event, value - 1);
                        }}
                      />
                    </Grid>
                  </Grid>
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </Grid>

        {/* Desktop view */}
        <Grid
          item
          md={12}
          xs={12}
          sx={{
            height: "78vh",
            overflowY: "auto",
            display: { xs: "none", md: "block" },
          }}
        >
          <Table
            sx={{
              borderCollapse: "separate",
              borderSpacing: " 0 8px",
            }}
          >
            <TableHead
              sx={{
                position: "sticky",
                zIndex: 5,
                top: 0,
                backgroundColor: theme.palette.dashboardBackground.main,
                width: "100%",
              }}
            >
              <TableRow>
                {[
                  { label: "Created date", noWrap: false, sortable: true },
                  { label: "Created by", noWrap: true },
                  { label: "Client", noWrap: true },
                  { label: "Vehicle", noWrap: true },
                  { label: "Departure Port", noWrap: true },
                  { label: "Arrival Port", noWrap: true },
                  { label: "Carrier", noWrap: true },
                  { label: "Crossing Date & Time", noWrap: true },
                  { label: "GMR ID", noWrap: true },
                  { label: "Inspection", noWrap: true },
                  { label: "GMR Status", noWrap: true, align: "center" },
                  { label: "Actions", noWrap: true, align: "right" },
                ].map((col) => (
                  <TableCell
                      key={col.label}
                      style={{
                        textAlign: col.align || "left",
                        color: "#8f8f8f",
                        paddingTop: "10px",
                        paddingBottom: "10px",
                        borderBottom: "none",
                        cursor: col.sortable ? "pointer" : "default",
                        userSelect: "none",
                      }}
                      onClick={col.sortable ? () => handleClickChangeOrderBy("dateCreated") : undefined}
                    >
                      <Typography
                        noWrap={col.noWrap}
                        variant={isMediumScreen ? "subtitle2" : "subtitle1"}
                        sx={{ display: "flex", alignItems: "center", gap: "4px" }}
                      >
                        {col.label}
                        {col.sortable && (
                          <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1, ml: "4px" }}>
                            <span style={{
                              fontSize: "10px",
                              lineHeight: "10px",
                              color: orderBy === "dateCreated" && orderDirection === "ASC" ? "#333" : "#ccc"
                            }}>▲</span>
                            <span style={{
                              fontSize: "10px",
                              lineHeight: "10px",
                              color: orderBy === "dateCreated" && orderDirection === "DESC" ? "#333" : "#ccc"
                            }}>▼</span>
                          </Box>
                        )}
                      </Typography>
                    </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading
                ? Array.from({ length: rowsPerPage }).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell colSpan={14} sx={{ padding: "12px 16px" }}>
                        <Skeleton
                          variant="rounded"
                          width="100%"
                          height={25}
                          animation="wave"
                        />
                      </TableCell>
                    </TableRow>
                  ))
                : declarations.length > 0 &&
                  declarations.map((item, index) => {
                    return (
                      <TableRow 
                        key={index}
                        style={{backgroundColor:
                                  item?.seen === false
                                    ? "rgb(66 185 178 / 21%)"
                                    : "",}
                        }
                        sx={{
                          transition: "transform 0.3s ease, box-shadow 0.3s ease",
                          transform: highlightedRow === item.id ? "translateY(-2px)" : "none",
                          boxShadow: highlightedRow === item.id
                            ? "0 8px 24px rgba(0,0,0,0.25)"
                            : "none",
                          outline: highlightedRow === item.id
                            ? "4px solid rgba(77, 77, 77, 0.37)"
                            : "none",
                          zIndex: highlightedRow === item.id ? 1 : 0,
                          position: "relative",
                        }} 
                        >
                        <TableCell>
                          <Typography
                            variant="h5"
                            sx={{ fontWeight: "light" }}
                          >
                            {item?.dateCreated
                              ? moment(item?.dateCreated).format(
                                  "DD.MM.YYYY HH:mm",
                                )
                              : "N/A"}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Tooltip
                            arrow
                            placement="top"
                            title={
                              item?.createdBy?.email
                                ? item?.createdBy?.email
                                : "N/A"
                            }
                          >
                            <Typography
                              variant="h5"
                              sx={{ fontWeight: "light" }}
                            >
                              {`${item?.createdBy?.firstName ?? ""} ${item?.createdBy?.lastName ?? ""}`}
                            </Typography>
                          </Tooltip>
                        </TableCell>

                        <TableCell>
                          <Typography
                            variant="h5"
                            sx={{ fontWeight: "light" }}
                          >
                            {item?.organization?.nameEn ?? "N/A"}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography
                            variant="h5"
                            sx={{ fontWeight: "light" }}
                          >
                            {item?.transport?.vehicleRegNum || "N/A"}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography
                            variant="h5"
                            sx={{ fontWeight: "light" }}
                          >
                            {item?.transport?.departurePort || "N/A"}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography
                            variant="h5"
                            sx={{ fontWeight: "light" }}
                          >
                            {item?.transport?.arrivalPort || "N/A"}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography
                            variant="h5"
                            sx={{ fontWeight: "light" }}
                          >
                            {item?.transport?.carrierCompany || "N/A"}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography
                            variant="h5"
                            sx={{ fontWeight: "light" }}
                          >
                            {item?.transport?.crossingDepartureDateTime
                              ? moment(
                                  item?.transport?.crossingDepartureDateTime,
                                ).format("DD.MM.YYYY HH:mm")
                              : "N/A"}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography
                            variant="h5"
                            sx={{ fontWeight: "light" }}
                          >
                            {item?.gmrId || "N/A"}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography
                            variant="h5"
                            sx={{ fontWeight: "light" }}
                          >
                            {item?.inspectionStart === null ||
                            item?.inspectionStart === undefined
                              ? "No"
                              : item?.inspectionEnd === null ||
                                  item?.inspectionEnd === undefined
                                ? "Yes (Active)"
                                : "No (Passed)"}
                          </Typography>
                        </TableCell>

                        <TableCell style={{ textAlign: "center" }}>
                          <Chip
                            style={{ width: "120px" }}
                            label={item?.gmrState || "N/A"}
                            color={getStatusColor(item?.gmrState)}
                          />
                        </TableCell>

                        <TableCell style={{ textAlign: "right" }}>
                          {item?.gmrId && (
                            <Tooltip title="Download PDF">
                              <IconButton
                                onClick={() => handleDownloadPdf(item?.id)}
                              >
                                <Icon
                                  style={{
                                    color: theme.palette.secondary.main,
                                  }}
                                  fontSize="medium"
                                >
                                  download
                                </Icon>
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Open declaration">
                            <IconButton
                              onClick={() => {
                                setEditingId(item?.id)
                                sessionStorage.setItem("highlightedRow", item?.id);
                                setHighlightedRow(item?.id);
                              }}
                            >
                              <Icon
                                style={{
                                  color: theme.palette.primary.main,
                                }}
                                fontSize="medium"
                              >
                                open_in_new
                              </Icon>
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
            </TableBody>

            <TableFooter
              style={{
                position: "fixed",
                bottom: 0,
                zIndex: 5,
                backgroundColor: theme.palette.dashboardBackground.main,
                width: "100%",
              }}
            >
              <Grid container spacing={1} sx={{ display: "flex" }}>
                <Grid item xs={4}>
                  <TablePagination
                    sx={{
                      borderBottom: "none",
                      "& .MuiTablePagination-select": {
                        fontSize: isMediumScreen ? "12px" : "18px",
                        border: "1px solid",
                        borderColor: theme.palette.lightGray.main,
                        padding: isMediumScreen ? "5px 8px" : "5px 15px",
                        borderRadius: "4px",
                      },
                      "& .MuiTablePagination-displayedRows": {
                        fontSize: isMediumScreen ? "15px" : "20px",
                      },
                      "& .MuiTablePagination-actions button": {
                        fontSize: isMediumScreen ? "15px" : "20px",
                      },
                      "& .MuiIconButton-root": {
                        fontSize: isMediumScreen ? "15px" : "20px",
                      },
                      "& .MuiSelect-icon": {
                        fontSize: isMediumScreen ? "15px" : "20px",
                      },
                      "& .MuiTablePagination-displayedRows": {
                        marginLeft: "-15px",
                        fontSize: isMediumScreen ? "12px" : "15px",
                        color: theme.palette.lightGray.main,
                      },
                    }}
                    rowsPerPageOptions={[5, 10, 15, 25, 50, 75, 100]}
                    rowsPerPage={rowsPerPage}
                    labelDisplayedRows={() => `of ${totalElements}`}
                    labelRowsPerPage={
                      <span
                        style={{
                          fontSize: isMediumScreen ? "12px" : "15px",
                          marginLeft: "-20px",
                          color: theme.palette.lightGray.main,
                        }}
                      >
                        Rows per page
                      </span>
                    }
                    SelectProps={{
                      inputProps: {
                        fontSize: isMediumScreen ? "15px" : "20px",
                        "aria-label": "Rows per page",
                      },
                      native: true,
                    }}
                    onRowsPerPageChange={(event) => {
                      handleChangeRowsPerPage(event);
                    }}
                    page={page}
                    count={totalElements}
                    size="large"
                    ActionsComponent={() => null}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Pagination
                    sx={{
                      marginTop: "15px",
                      display: "flex",
                      justifyContent: "center",
                      "& .MuiPaginationItem-root": {
                        fontSize: isMediumScreen ? "15px" : "20px",
                      },
                      "& .MuiPaginationItem-page.Mui-selected": {
                        backgroundColor: theme.palette.secondary.main,
                        opacity: "50%",
                        color: "white",
                      },
                      "& .MuiPaginationItem-previousNext": {
                        border: "1px solid",
                        borderColor: theme.palette.lightGray.main,
                      },
                    }}
                    count={
                      (totalElements % rowsPerPage === 0
                        ? totalElements / rowsPerPage
                        : Math.floor(totalElements / rowsPerPage) + 1) || 0
                    }
                    shape="rounded"
                    size={isMediumScreen ? "medium" : "large"}
                    page={page + 1 || 0}
                    onChange={(event, value) => {
                      handleChangePage(event, value - 1);
                    }}
                  />
                </Grid>
              </Grid>
            </TableFooter>
          </Table>
        </Grid>
      </Grid>

      {(openForm || editingId !== null) && (
        <GvmsDeclarationForm
          key={editingId || "new"}
          editingId={editingId}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
          onDuplicate={handleDuplicate}
        />
      )}

      <Dialog
        open={Boolean(deletingDeclaration)}
        onClose={() => setDeletingDeclaration(null)}
        maxWidth="sm"
        fullWidth
      >
        <div style={{ minHeight: "4px" }} />
        <DialogContent>
          <Typography>
            Are you sure you want to delete the declaration for{" "}
            <strong>
              {deletingDeclaration?.organization?.nameEn ??
                deletingDeclaration?.gmrId ??
                "this record"}
            </strong>{" "}
            from the local database only?
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            This will <strong>not</strong> delete the declaration from Conex.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingDeclaration(null)} color="inherit">
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Delete Locally
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deletingFromConex)}
        onClose={() => setDeletingFromConex(null)}
        maxWidth="sm"
        fullWidth
      >
        <div style={{ minHeight: "4px" }} />
        <DialogContent>
          <Typography>
            Are you sure you want to delete the declaration for{" "}
            <strong>
              {deletingFromConex?.organization?.nameEn ??
                deletingFromConex?.gmrId ??
                "this record"}
            </strong>{" "}
            from Conex? This will also remove it locally.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingFromConex(null)} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteFromConex}
          >
            Delete from Conex
          </Button>
        </DialogActions>
      </Dialog>
      <Outlet />
    </>
  );
}